import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import {
  ConversationMessages,
  useConversationMessages,
} from '@/components/admin/ConversationThread';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { taxYearLabel } from '@/lib/utils';
import type {
  AgentConversationSummary,
  AdminModuleAgentConversationsResponse,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/agent/conversations')({
  component: AgentConversations,
});

function AgentConversations() {
  const [selectedReturnId, setSelectedReturnId] = useState<number | null>(null);

  const summaries = useQuery({
    queryKey: ['admin', 'agent', 'conversations'],
    queryFn: () =>
      api<AdminModuleAgentConversationsResponse>(
        '/admin/agent/conversations?pageNumber=1&pageSize=100',
      ),
    retry: false,
  });
  const items: AgentConversationSummary[] = summaries.data?.items ?? [];

  const columns = useMemo<ColumnDef<AgentConversationSummary>[]>(
    () => [
      {
        id: 'taxpayer',
        accessorFn: (s) => s.taxpayerName ?? `Return #${s.taxReturnId}`,
        header: 'Taxpayer',
        cell: ({ row }) => (
          <span className="font-medium text-(--color-ink)">
            {row.original.taxpayerName ?? `Return #${row.original.taxReturnId}`}
          </span>
        ),
      },
      {
        id: 'year',
        accessorFn: (s) => Number(s.year),
        header: 'Year',
        cell: ({ row }) => (
          <span className="text-(--color-ink-2) tabular-nums">FY {taxYearLabel(Number(row.original.year))}</span>
        ),
      },
      {
        id: 'messages',
        accessorFn: (s) => Number(s.messageCount ?? 0),
        header: 'Messages',
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--color-ink)">{Number(row.original.messageCount ?? 0)}</span>
        ),
      },
      {
        id: 'compacted',
        accessorFn: (s) => Number(s.compactedCount ?? 0),
        header: 'Compacted',
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--color-ink-muted)">
            {Number(row.original.compactedCount ?? 0)}
          </span>
        ),
      },
      {
        id: 'toolcalls',
        accessorFn: (s) => Number(s.toolCallsLast7d ?? 0),
        header: 'Tool calls (7d)',
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--color-ink-muted)">
            {Number(row.original.toolCallsLast7d ?? 0)}
          </span>
        ),
      },
      {
        id: 'lastturn',
        accessorFn: (s) => (s.lastTurnAt as string | null) ?? '',
        header: 'Last turn',
        cell: ({ row }) => {
          const lt = row.original.lastTurnAt as string | null | undefined;
          return (
            <span className="font-mono text-[13px] tabular-nums text-(--color-ink-muted)">
              {lt ? new Date(lt).toLocaleString('en-AU') : '—'}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <DataTable
        columns={columns}
        data={items}
        isLoading={summaries.isLoading}
        loadingMessage="Loading conversations…"
        emptyMessage={summaries.isError ? 'Could not load conversation summaries.' : 'No conversations yet.'}
        getRowId={(s) => String(s.taxReturnId)}
        onRowClick={(s) => setSelectedReturnId(Number(s.taxReturnId))}
        initialSorting={[{ id: 'lastturn', desc: true }]}
      />

      <ConversationDrawer returnId={selectedReturnId} onClose={() => setSelectedReturnId(null)} />
    </div>
  );
}

function ConversationDrawer({
  returnId,
  onClose,
}: {
  returnId: number | null;
  onClose: () => void;
}) {
  const open = returnId !== null;
  const { messages, isLoading, isError, truncated } = useConversationMessages(open ? returnId : null);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent variant="side" className="flex flex-col">
        <div className="border-b border-(--color-rule) px-7 py-6">
          <div className="section-ref">Conversation</div>
          <DialogTitle className="mt-1">
            <Link
              to="/admin/returns/$id"
              params={{ id: String(returnId ?? '') }}
              className="gov-link"
            >
              Return #{returnId}
            </Link>
          </DialogTitle>
          <DialogDescription className="mt-1">
            {messages.length > 0
              ? `${messages.length} message${messages.length === 1 ? '' : 's'}, oldest first. Tool calls are collapsed.`
              : 'Tool calls are collapsed by default.'}
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto bg-(--color-paper) px-5 py-6 scrollbar-thin">
          <ConversationMessages
            messages={messages}
            isLoading={isLoading}
            isError={isError}
            truncated={truncated}
            autoScroll
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
